import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchTopics } from '../../topics/api/topicsClient'
import { fetchPosts } from '../../posts/api/postsClient'
import type { TopicList } from '../../topics/model/contracts'
import type { PostDto } from '../../posts/model/contracts'
import type { TopicStats, DailyPoint } from '../model/contracts'

function deriveStats(topic: TopicList, posts: PostDto[]): TopicStats {
  const byDate = new Map<string, number>()
  const authors = new Set<string>()
  let positiveCount = 0

  for (const post of posts) {
    const day = post.timestamp.slice(0, 10)
    byDate.set(day, (byDate.get(day) ?? 0) + 1)
    authors.add(post.author.handle ?? post.author.name)
    if (post.sentiment === 'positive') positiveCount++
  }

  const series: DailyPoint[] = Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date, count }))

  return {
    id: topic.id,
    name: topic.name,
    slug: topic.slug,
    totalMentions: posts.length,
    uniqueAuthors: authors.size,
    positivePct: posts.length > 0 ? Math.round((positiveCount / posts.length) * 100) : 0,
    series,
  }
}

export function useTopicCompare() {
  const [searchParams, setSearchParams] = useSearchParams()
  const slugA = searchParams.get('a') ?? ''
  const slugB = searchParams.get('b') ?? ''

  const topicsQuery = useQuery({
    queryKey: ['topics', { limit: 100 }],
    queryFn: ({ signal }) => fetchTopics({ limit: 100 }, signal),
  })

  const allTopics = useMemo(() => topicsQuery.data?.data ?? [], [topicsQuery.data])

  const topicA = useMemo(
    () => allTopics.find((t) => t.slug === slugA || t.id === slugA) ?? null,
    [allTopics, slugA],
  )
  const topicB = useMemo(
    () => allTopics.find((t) => t.slug === slugB || t.id === slugB) ?? null,
    [allTopics, slugB],
  )

  const postsAQuery = useQuery({
    queryKey: ['compare-posts', topicA?.id],
    queryFn: ({ signal }) =>
      fetchPosts({ keywords: topicA!.keywords, limit: 500 }, signal),
    enabled: !!topicA,
  })

  const postsBQuery = useQuery({
    queryKey: ['compare-posts', topicB?.id],
    queryFn: ({ signal }) =>
      fetchPosts({ keywords: topicB!.keywords, limit: 500 }, signal),
    enabled: !!topicB,
  })

  const statsA = useMemo(
    () =>
      topicA && postsAQuery.data ? deriveStats(topicA, postsAQuery.data.items) : null,
    [topicA, postsAQuery.data],
  )

  const statsB = useMemo(
    () =>
      topicB && postsBQuery.data ? deriveStats(topicB, postsBQuery.data.items) : null,
    [topicB, postsBQuery.data],
  )

  function setSlugA(slug: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('a', slug)
      return next
    })
  }

  function setSlugB(slug: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('b', slug)
      return next
    })
  }

  return {
    allTopics,
    slugA,
    slugB,
    topicA,
    topicB,
    statsA,
    statsB,
    isLoadingTopics: topicsQuery.isLoading,
    isLoadingA: postsAQuery.isFetching,
    isLoadingB: postsBQuery.isFetching,
    setSlugA,
    setSlugB,
  }
}
